import { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Textarea } from '../../components/ui/textarea';

/**
 * "Who we are" — the brand story.
 *
 * One field, because one field is what the section reads: `description`. The
 * facts rail beside it (website, location, created) is drawn from fields that
 * belong to other screens, so editing them here would mean two places writing
 * the same thing.
 */
export function EditBrandStoryModal({
	open,
	onOpenChange,
	description,
	onSave,
	loading,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	description?: string | null;
	onSave: (description: string) => void;
	loading?: boolean;
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[560px]">
				<DialogHeader>
					<DialogTitle>Edit brand story</DialogTitle>
					<DialogDescription>
						What the brand is and who it is for. This is what agencies and suppliers
						read first.
					</DialogDescription>
				</DialogHeader>

				{/* The form is mounted by opening, so the draft starts from what the
				    brand says right now. Seeding a long-lived form from an effect
				    instead would re-seed on every parent render — including the one
				    a refetch causes — and wipe what was being typed. */}
				{open && (
					<StoryForm
						initial={description ?? ''}
						onCancel={() => onOpenChange(false)}
						onSave={onSave}
						loading={loading}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

function StoryForm({
	initial,
	onCancel,
	onSave,
	loading,
}: {
	initial: string;
	onCancel: () => void;
	onSave: (description: string) => void;
	loading?: boolean;
}) {
	const [value, setValue] = useState(initial);

	return (
		<DialogForm onSubmit={() => onSave(value.trim())}>
			<DialogBody>
				<Field label="Brand story">
					<Textarea
						value={value}
						onChange={(e) => setValue(e.target.value)}
						rows={10}
						placeholder="Tell the story of the brand — where it came from, what it stands for, who it is for."
					/>
				</Field>
			</DialogBody>

			<DialogFooter>
				<Button type="button" variant="outline" onClick={onCancel}>
					Cancel
				</Button>
				<Button type="submit" disabled={loading}>
					{loading ? 'Saving...' : 'Save'}
				</Button>
			</DialogFooter>
		</DialogForm>
	);
}
